// ABOUTME: Top navigation bar for Drafty
// ABOUTME: Contains logo, search, notifications bell, and create button
// ABOUTME: Scrolls away when user scrolls down, reappears when scrolling up

"use client"

import { useState, useEffect, useRef } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBell, faPlus, faBars } from "@fortawesome/free-solid-svg-icons"
import { DraftyLogo } from "../drafty-logo"
import { CreateMenu } from "./create-menu"
import { MobileMenu } from "./mobile-menu"
import { useAddLinkModal } from "@/contexts/add-link-modal-context"

const SCROLL_THRESHOLD = 100 // Always show when within this many pixels of top

interface TopBarProps {
  isAuthenticated: boolean
  username?: string
  unreadNotificationsCount?: number
  showOnDesktop?: boolean
}

export function TopBar({ isAuthenticated, username, unreadNotificationsCount = 0, showOnDesktop = false }: TopBarProps) {
  const pathname = usePathname()
  const [createMenuOpen, setCreateMenuOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const lastScrollY = useRef(0)
  const { openAddLinkModal } = useAddLinkModal()

  // Handle scroll to show/hide the top bar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Always show at top of page
      if (currentScrollY < SCROLL_THRESHOLD) {
        setIsVisible(true)
        lastScrollY.current = currentScrollY
        return
      }

      // Scrolling down - hide
      if (currentScrollY > lastScrollY.current) {
        setIsVisible(false)
      }
      // Scrolling up - show
      else if (currentScrollY < lastScrollY.current) {
        setIsVisible(true)
      }

      lastScrollY.current = currentScrollY
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleOpenAddLink = () => {
    setCreateMenuOpen(false)
    openAddLinkModal()
  }

  // Don't show on certain pages
  const shouldHide =
    pathname === "/" ||
    pathname === "/sign-in" ||
    pathname === "/sign-up" ||
    pathname === "/new" ||
    pathname === "/bookmarks/add" ||
    (pathname.includes("/posts/") && pathname.endsWith("/edit"))

  if (shouldHide) return null

  return (
    <>
      <header className={`${showOnDesktop ? '' : 'md:hidden'} fixed top-0 left-0 right-0 h-14 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-sm border-b border-neutral-200/50 dark:border-neutral-800/50 z-50 transition-transform duration-300 ${isVisible ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="h-full px-4 flex items-center justify-between">
          {/* Left: Logo */}
          <Link href="/home" className="flex items-center">
            <DraftyLogo width={90} height={16} />
          </Link>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {isAuthenticated && (
              <>
                {/* Notifications Bell */}
                <Link
                  href="/notifications"
                  className={`${showOnDesktop ? '' : 'md:hidden'} relative w-10 h-10 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-neutral-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors`}
                >
                  <FontAwesomeIcon icon={faBell} className="w-5 h-5" />
                  {unreadNotificationsCount > 0 && (
                    <span className="absolute top-1 right-1 min-w-[16px] h-[16px] flex items-center justify-center px-1 text-[10px] font-medium text-white bg-pink-600 rounded-full">
                      {unreadNotificationsCount > 99 ? "99+" : unreadNotificationsCount}
                    </span>
                  )}
                </Link>

                {/* Create Button */}
                <button
                  onClick={() => setCreateMenuOpen(!createMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium"
                >
                  <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                  <span className="hidden sm:inline">New</span>
                </button>

                {/* Hamburger Menu */}
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className={`${showOnDesktop ? '' : 'md:hidden'} w-10 h-10 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-neutral-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors`}
                  aria-label="Open menu"
                >
                  <FontAwesomeIcon icon={faBars} className="w-5 h-5" />
                </button>
              </>
            )}

            {!isAuthenticated && (
              <div className="flex items-center gap-2">
                <Link
                  href="/sign-in"
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  href="/sign-up"
                  className="px-4 py-2 text-sm font-medium bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Create Menu (Bottom Sheet) */}
      <CreateMenu
        isOpen={createMenuOpen}
        onClose={() => setCreateMenuOpen(false)}
        onOpenAddLink={handleOpenAddLink}
        isMobile={true}
        showOnDesktop={showOnDesktop}
      />

      {/* Menu */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        username={username}
        unreadNotificationsCount={unreadNotificationsCount}
        showOnDesktop={showOnDesktop}
      />
    </>
  )
}
